import { useEffect, useState } from 'react'

import {
  PaymentRequestResponse,
  CustomerFindInvoiceFragment,
  InvoiceStatus,
  useCustomerGetPaymentRequestLazyQuery,
  useFindInvoiceLazyQuery,
  PaymentRequestStatusEnum
} from 'graph/generated/payments/graphql-types'
import { useRouter } from 'next/router'
import { formatDate } from 'utils/dates'

import useActiveUserInfo from '@/hooks/useActiveUserInfo'

const usePaymentRequestInvoiceData = (): {
  invoice: CustomerFindInvoiceFragment
  loading: boolean
  isPaymentRequest: boolean
  paymentRequestID: string
  setInvoiceData: (invoice: Partial<CustomerFindInvoiceFragment>) => void
  paymentRequestData: PaymentRequestResponse
} => {
  const { customer } = useActiveUserInfo()
  const {
    query: { id: paymentRequestID },
    pathname,
    push
  } = useRouter()

  const [invoice, setInvoice] = useState<CustomerFindInvoiceFragment | null>({
    __typename: 'Invoice',
    id: '',
    invoiceNumber: '',
    amount: 0,
    outstandingAmount: 0,
    status: InvoiceStatus.Upcoming,
    createdAt: formatDate(new Date(), 'YYYY-MM-DD', 'local'),
    dueDate: formatDate(new Date(), 'YYYY-MM-DD', 'local'),
    customer: {
      __typename: 'Customer',
      id: customer?.id,
      name: customer?.name
    },
    offer: null,
    lending: null,
    productName: '',
    recurringpayment: false,
    totalTaxAmount: 0,
    lineItems: [],
    platformName: '',
    totalDiscountAmount: 0
  })
  const [
    fetchPaymentRequest,
    { data: paymentRequestData, loading: paymentRequestLoading }
  ] = useCustomerGetPaymentRequestLazyQuery({
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      setInvoice(prev => ({
        ...prev,
        amount: data.CustomerGetPaymentRequest?.amount,
        outstandingAmount: data.CustomerGetPaymentRequest?.amount
      }))
    }
  })
  const [fetchInvoice] = useFindInvoiceLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: data => {
      setInvoice(data.FindInvoice)
    }
  })

  const isPaymentRequest = pathname.includes('/payment-request')
  const isNotStatusPage = !pathname.includes('/status')

  useEffect(() => {
    if (!isPaymentRequest || !paymentRequestID) return
    fetchPaymentRequest({
      variables: {
        paymentRequestID: paymentRequestID as string
      }
    })
  }, [fetchPaymentRequest, isPaymentRequest, paymentRequestID])

  useEffect(() => {
    if (
      !isPaymentRequest ||
      !paymentRequestID ||
      !paymentRequestData?.CustomerGetPaymentRequest?.invoiceID
    )
      return
    fetchInvoice({
      variables: {
        id: paymentRequestData?.CustomerGetPaymentRequest?.invoiceID as string
      }
    })
  }, [fetchInvoice, isPaymentRequest, paymentRequestID, paymentRequestData])

  useEffect(() => {
    const paymentRequest = paymentRequestData?.CustomerGetPaymentRequest
    const paymentRequestAlreadyHasInvoice = paymentRequest?.invoiceID

    // if the payment request already has an invoice and the status is processing or succeeded, we redirect to the status page
    // this is to avoid the user to try to pay the same payment-request multiple times
    if (
      paymentRequestAlreadyHasInvoice &&
      isPaymentRequest &&
      isNotStatusPage &&
      [
        PaymentRequestStatusEnum.Processing,
        PaymentRequestStatusEnum.Succeeded
      ].includes(paymentRequest?.status)
    ) {
      push(`/payment-request/${paymentRequestID}/status`)
    }
  }, [isNotStatusPage, paymentRequestData])

  return {
    invoice,
    loading: paymentRequestLoading,
    isPaymentRequest,
    paymentRequestID: paymentRequestID as string,
    setInvoiceData: (invoice: Partial<CustomerFindInvoiceFragment>) =>
      setInvoice(prev => ({ ...prev, ...invoice })),
    paymentRequestData: paymentRequestData?.CustomerGetPaymentRequest
  }
}

export default usePaymentRequestInvoiceData
